import { useState, useEffect } from 'react'
import { useQuery, useQueryClient } from 'react-query'
import { searchRocketReach } from 'services/rocketReach'
import { getErrorMessage } from 'constants/rocketReachErrors'
import usePagination from 'hooks/usePagination'
import { useNotification } from 'components/Notification'

const getSearchQueryKey = (searchForm, pagination) => {
  return [
    'rocketReach',
    'search',
    {
      searchBy: searchForm.searchBy,
      searchTerm: searchForm.searchTerm,
      pageOffset: pagination.pageOffset,
      pageSize: pagination.pageSize,
      ...(searchForm.searchKeywords && { keywords: searchForm.searchKeywords }),
    },
  ]
}

/**
 * RocketReach search hook - handles search functionality only
 * @returns {object} - search results and search controls
 */
const useRocketReachSearch = () => {
  const queryClient = useQueryClient()
  const { pagination, handlePageChange, handleRowsChange } = usePagination()
  const { createNotification } = useNotification()

  const [searchForm, setSearchForm] = useState({
    searchBy: '',
    searchTerm: '',
    searchKeywords: '',
  })
  const [lastErrorMessage, setLastErrorMessage] = useState(null)

  // Search query
  const {
    data: searchResult,
    isLoading: isSearchLoading,
    isFetching: isSearchFetching,
    isError: isSearchError,
  } = useQuery({
    queryKey: getSearchQueryKey(searchForm, pagination),
    queryFn: () => {
      return searchRocketReach({
        pageOffset: pagination.pageOffset,
        pageSize: pagination.pageSize,
        [searchForm.searchBy]: searchForm.searchTerm,
        ...(searchForm.searchKeywords && { keywords: searchForm.searchKeywords }),
      })
    },
    initialData: {
      data: [],
      meta: {
        itemCount: 0,
      },
    },
    enabled: Boolean(searchForm.searchBy && searchForm.searchTerm),
    retry: false,
  })

  // Handle search success/error with useEffect
  useEffect(() => {
    if (searchResult && searchResult.error) {
      const errorMessage = getErrorMessage(searchResult.error)

      if (errorMessage !== lastErrorMessage) {
        setLastErrorMessage(errorMessage)
        createNotification({
          message: errorMessage,
          variant: 'error',
        })
      }
    } else if (searchResult && searchResult.data) {
      if (lastErrorMessage) {
        setLastErrorMessage(null)
      }
    }
  }, [searchResult, lastErrorMessage, createNotification])

  const handleSetPage = page => {
    handlePageChange(page)
  }

  const handleSetRowsPerPage = pageSize => {
    handleRowsChange(pageSize.rowsPerPage)
  }

  const handleSearch = newSearchForm => {
    setSearchForm(newSearchForm)

    // Reset pagination when search changes
    handlePageChange(0)

    // Invalidate the search query to force a new fetch since handleSearch is called on search button click otherwise it wont fetch new data
    queryClient.invalidateQueries(getSearchQueryKey(newSearchForm, pagination))
  }

  return {
    state: {
      data: searchResult?.data || [],
      total: searchResult?.meta?.itemCount || 0,
      page: pagination.page,
      rowsPerPage: pagination.pageSize,
      isLoading: isSearchLoading || isSearchFetching,
      failed: isSearchError || (searchResult && searchResult.error),
    },
    setPage: handleSetPage,
    setRowsPerPage: handleSetRowsPerPage,
    handleSearch,
  }
}

export default useRocketReachSearch
